import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, GrGameConfigDetail, GrGameConfigReviewStatus } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGameConfigsOptions {
  reviewStatus?: GrGameConfigReviewStatus;
}

interface UseGameConfigsReturn {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useGameConfigs(options?: UseGameConfigsOptions): UseGameConfigsReturn {
  const filters = options?.reviewStatus ? { reviewStatus: options.reviewStatus } : undefined;
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.gameRewards.gameConfigs(filters),
    queryFn: () => api.listGameRewardsGames(filters),
    staleTime: 5 * 60 * 1000, // Game configs are admin-controlled, rarely change
  });

  return {
    gameConfigs: data?.gameConfigs ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : isError ? 'Failed to load game configs' : null,
    refresh: () => {
      refetch();
    },
  };
}

interface ApproveGameMutationVars {
  gameConfigId: string;
}

interface RejectGameMutationVars {
  gameConfigId: string;
  reason?: string;
}

/**
 * Mutations for the admin approval flow. Invalidate the game-config list on
 * success so any open list view refreshes immediately.
 */
export function useApproveGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId }: ApproveGameMutationVars) =>
      api.approveGameRewardsGame(gameConfigId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.all });
    },
  });
}

export function useRejectGameConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ gameConfigId, reason }: RejectGameMutationVars) =>
      api.rejectGameRewardsGame(gameConfigId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.all });
    },
  });
}
